
import React, { useState, useEffect } from 'react';
import { databaseService } from '../services/databaseService';
import { generateBakeryLogo } from '../services/imageService';
import { Subscriber } from '../types';
import { X, Trash2, Download, Users, Mail, Calendar, Palette, Sparkles, Loader2, Check, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface AdminPanelProps {
  onClose: () => void;
  currentLogo: string;
  onLogoChange: (newLogo: string) => void;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ onClose, currentLogo, onLogoChange }) => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [activeTab, setActiveTab] = useState<'subscribers' | 'branding'>('subscribers');
  
  // Estados para Branding
  const [logoPrompt, setLogoPrompt] = useState('artesanal y moderno, colores cálidos dorados y marrones, con un toque de verde para frescura');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewLogo, setPreviewLogo] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    setSubscribers(databaseService.getAllSubscribers());
  }, []);

  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => setStatusMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [statusMessage]);

  const handleDelete = (id: string) => {
    databaseService.removeSubscriber(id);
    setSubscribers(databaseService.getAllSubscribers());
    setDeleteConfirm(null);
    setStatusMessage({ type: 'success', text: 'Registro eliminado' });
  };

  const handleExport = () => {
    const csv = databaseService.exportToCSV();
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('hidden', '');
    a.setAttribute('href', url);
    a.setAttribute('download', `suscriptores_artesano_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleGenerateLogo = async () => {
    setIsGenerating(true);
    try {
      const newLogoData = await generateBakeryLogo(logoPrompt);
      setPreviewLogo(newLogoData);
    } catch (error) {
      setStatusMessage({ type: 'error', text: 'Error al generar el logo' });
    } finally {
      setIsGenerating(false);
    }
  };

  const applyNewLogo = () => {
    if (previewLogo) {
      onLogoChange(previewLogo);
      setPreviewLogo(null);
      setStatusMessage({ type: 'success', text: 'Identidad actualizada' });
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] bg-stone-900/40 backdrop-blur-sm flex items-center justify-center p-6"
    >
      <motion.div 
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white w-full max-w-4xl max-h-[85vh] rounded-[2rem] shadow-2xl overflow-hidden flex flex-col relative"
      >
        {/* Toast Notification */}
        <AnimatePresence>
          {statusMessage && (
            <motion.div 
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 20, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              className={`absolute top-0 left-1/2 -translate-x-1/2 z-[130] px-6 py-3 rounded-full shadow-lg flex items-center gap-2 ${
                statusMessage.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'
              }`}
            >
              {statusMessage.type === 'success' ? <Check size={18} /> : <AlertCircle size={18} />}
              <span className="text-xs font-bold uppercase tracking-widest">{statusMessage.text}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header del Panel */}
        <div className="p-8 border-b border-stone-100 flex justify-between items-center bg-stone-50">
          <div className="flex items-center gap-6">
            <button 
              onClick={() => setActiveTab('subscribers')}
              className={`flex items-center gap-2 transition-all ${activeTab === 'subscribers' ? 'text-stone-900 border-b-2 border-amber-400 pb-1' : 'text-stone-400 hover:text-stone-600'}`}
            >
              <Users size={18} />
              <span className="text-sm font-bold uppercase tracking-tighter">Suscriptores</span>
            </button>
            <button 
              onClick={() => setActiveTab('branding')}
              className={`flex items-center gap-2 transition-all ${activeTab === 'branding' ? 'text-stone-900 border-b-2 border-amber-400 pb-1' : 'text-stone-400 hover:text-stone-600'}`}
            >
              <Palette size={18} />
              <span className="text-sm font-bold uppercase tracking-tighter">Branding IA</span>
            </button>
          </div>
          <div className="flex gap-4">
            {activeTab === 'subscribers' && (
              <button 
                onClick={handleExport}
                className="flex items-center gap-2 bg-stone-900 text-white px-5 py-2 rounded-full text-[10px] font-bold uppercase tracking-widest hover:bg-stone-700 transition-all shadow-lg"
              >
                <Download size={14} /> Exportar CSV
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-stone-200 rounded-full transition-colors">
              <X size={20} className="text-stone-500" />
            </button>
          </div>
        </div>

        {/* Contenido Principal */}
        <div className="flex-grow overflow-y-auto p-8">
          {activeTab === 'subscribers' ? (
            <div className="space-y-6">
              {subscribers.length === 0 ? (
                <div className="text-center py-20">
                  <Mail className="mx-auto text-stone-200 mb-4" size={48} />
                  <p className="text-stone-400 font-light italic">Aún no hay suscriptores registrados.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400 border-b border-stone-100">
                      <tr>
                        <th className="px-4 py-4">Email</th>
                        <th className="px-4 py-4">Fecha de Registro</th>
                        <th className="px-4 py-4 text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-50">
                      {subscribers.map((sub) => (
                        <tr key={sub.id} className="hover:bg-stone-50/50 transition-colors">
                          <td className="px-4 py-4 font-semibold text-stone-800">{sub.email}</td>
                          <td className="px-4 py-4 text-stone-500 font-light flex items-center gap-2">
                            <Calendar size={12} />
                            {new Date(sub.joinedAt).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="px-4 py-4 text-center">
                            {deleteConfirm === sub.id ? (
                              <div className="flex items-center justify-center gap-2">
                                <button onClick={() => handleDelete(sub.id)} className="text-rose-500 hover:text-rose-700 font-bold text-[10px] uppercase">Confirmar</button>
                                <button onClick={() => setDeleteConfirm(null)} className="text-stone-400 hover:text-stone-600 font-bold text-[10px] uppercase">No</button>
                              </div>
                            ) : (
                              <button 
                                onClick={() => setDeleteConfirm(sub.id)}
                                className="text-stone-300 hover:text-rose-500 transition-colors p-2"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            /* SECCIÓN DE BRANDING */
            <div className="max-w-2xl mx-auto space-y-10">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-4">
                  <Sparkles size={14} /> Inteligencia Creativa
                </div>
                <h3 className="text-2xl font-bold text-stone-900 mb-2">Diseñador de Logo Arte-Sano</h3>
                <p className="text-stone-500 text-sm font-light leading-relaxed">
                  Genera una identidad visual única basada en tu visión gastronómica.
                </p>
              </div>

              <div className="space-y-4">
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-400 ml-1">Descripción del Estilo</label>
                <textarea 
                  value={logoPrompt}
                  onChange={(e) => setLogoPrompt(e.target.value)}
                  className="w-full bg-stone-50 border border-stone-200 rounded-2xl p-4 text-sm focus:ring-2 focus:ring-amber-400 focus:outline-none min-h-[100px] transition-all"
                  placeholder="Describe cómo quieres que sea el logo..."
                />
                <button 
                  onClick={handleGenerateLogo}
                  disabled={isGenerating}
                  className="w-full bg-stone-900 text-white py-4 rounded-full font-bold text-xs uppercase tracking-[0.2em] hover:bg-stone-700 transition-all disabled:opacity-50 flex items-center justify-center gap-3 shadow-xl"
                >
                  {isGenerating ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                  {isGenerating ? 'Elaborando Identidad...' : 'Generar Logo con IA'}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-8 pt-6 border-t border-stone-100">
                <div className="space-y-4">
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-stone-400">Logo Actual</h4>
                  <div className="aspect-square bg-white border border-stone-100 rounded-3xl flex items-center justify-center p-8 shadow-inner overflow-hidden">
                    <img src={currentLogo} alt="Logo Actual" className="max-w-full h-auto object-contain" />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Nueva Propuesta</h4>
                  <div className="aspect-square bg-stone-50 border-2 border-dashed border-stone-200 rounded-3xl flex items-center justify-center p-8 overflow-hidden relative group">
                    {previewLogo ? (
                      <>
                        <img src={previewLogo} alt="Preview" className="max-w-full h-auto object-contain animate-in zoom-in-95" />
                        <button 
                          onClick={applyNewLogo}
                          className="absolute inset-0 bg-stone-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white gap-2"
                        >
                          <Check className="bg-amber-400 text-stone-900 rounded-full p-2" size={32} />
                          <span className="text-[10px] font-bold uppercase tracking-widest">Aplicar Logo</span>
                        </button>
                      </>
                    ) : (
                      <div className="text-center text-stone-300 px-6">
                        <Palette size={40} className="mx-auto mb-2 opacity-20" />
                        <p className="text-[10px] font-bold uppercase tracking-widest leading-relaxed">El nuevo diseño aparecerá aquí</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="p-6 bg-stone-50 border-t border-stone-100 text-center">
          <p className="text-[10px] font-bold text-stone-400 uppercase tracking-[0.3em]">Gestión de Activos Digitales • Arte-Sano ©</p>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AdminPanel;
